/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  byKeys,
  filterBy,
  mapBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

export default class LatestController extends Controller {
  Jurisdiction = Jurisdiction;

  @service
  session;

  @service
  dates;

  @service
  personOptions;

  learnersSortOrder = Object.freeze(['firstName', 'lastName']);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  @alias('session.school')
  school;

  @alias('school.jurisdiction')
  jurisdiction;

  @computed('model.group.name', 'session.school.name')
  get exportTitle() {
    return `${this.get(
      'session.school.name',
    )}--${this.model.group.name}--Expectation Snapshots and Progress--Curriculum Levels`;
  }

  @tracked
  selectedMilestoneSet = null;
  filterField = 'goalSubject.id';

  @computed('model.goalSubjects.[]', 'selectedMilestoneSet.{id,isDefault}')
  get subjects() {
    let subjects = this.model.goalSubjects;
    if (isEmpty(this.selectedMilestoneSet)) {
      // This should only happen before the milestone-set-filter component
      // has determined which Milestone Set to select by default.
      return subjects;
    }
    if (this.selectedMilestoneSet.isDefault) {
      // Hide any subjects which are using a Milestone Set.
      subjects = subjects.filter((item) => !item.milestone_set_id);
    } else {
      subjects = subjects.filter(
        (item) => item.milestone_set_id === this.selectedMilestoneSet.id,
      );
    }
    return subjects
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed
  get summaryFields() {
    if (this.jurisdiction === Jurisdiction.AU_NSW) {
      return Object.freeze([
        { label: 'ATSI', field: 'atsi' },
        { label: 'Gender', field: 'gender' },
      ]);
    }
    return Object.freeze([
      { label: 'Ethnicity', field: 'displayEthnicity' },
      { label: 'Gender', field: 'gender' },
    ]);
  }

  @computed('subjects.[]', 'sortedLearners.[]')
  get learnerMilestones() {
    let members = this.sortedLearners;
    if (!isArray(members)) {
      return {};
    }
    return members.reduce((map, person) => {
      map[person.id] = this.subjects.reduce((subjectMap, subject) => {
        subjectMap[subject.id] = getCurrentMilestone(
          subject.milestones,
          person,
          this.dates.rolloverMonthDate,
        );
        return subjectMap;
      }, {});
      return map;
    }, {});
  }

  @computed('model.{progressions.[],learners.[]}', 'subjects.[]')
  get learnerProgressions() {
    let members = this.get('model.learners') ?? [];
    if (members.length === 0) {
      // No current learners in group - don't bother trying to map anything.
      return {};
    }

    // Build initial empty map of personId -> subjectId -> array of progressions.
    let subjects = this.subjects.reduce((map, subject) => {
      map[subject.id] = [];
      return map;
    }, {});
    let subjectsJSON = JSON.stringify(subjects);
    let map = members.reduce((map, person) => {
      map[person.id] = JSON.parse(subjectsJSON);
      return map;
    }, {});

    // Populate with records.
    let progressions = this.get('model.progressions') ?? [];
    if (progressions.length === 0) {
      // No progression records - just return the empty map.
      return map;
    }

    // Pre-sort progression records in the order we want them to end up.
    return progressions
      .slice()
      .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
      .reverse()
      .reduce((map, progression) => {
        let personId = progression.person_id;
        let subjectId = progression.goal_subject_id;
        // Ignore people who aren't current group members, and subjects which we aren't showing.
        if (!isEmpty(map[personId]) && isArray(map[personId][subjectId])) {
          map[personId][subjectId].push(progression);
        }
        return map;
      }, map);
  }

  @computed(
    'model.{goalSubjects.[],learners.[]}',
    'learnerProgressions',
    'learnerMilestones',
  )
  get learnerLevels() {
    let members = this.get('model.learners') ?? [];
    if (members.length === 0) {
      // No current learners in group - don't bother trying to map anything.
      return {};
    }

    // Build initial empty map of personId -> subjectId -> current level.
    return members.reduce((map, person) => {
      let milestones = this.get(`learnerMilestones.${person.id}`);
      map[person.id] = this.subjects.reduce((subjectMap, subject) => {
        subjectMap[subject.id] = this.computeCurrentLevel(
          person,
          subject.id,
          milestones[subject.id],
        );
        return subjectMap;
      }, {});
      return map;
    }, {});
  }

  @computed('sortedLearners.[]', 'learnerLevels', 'subjects.[]')
  get exportData() {
    return (this.sortedLearners ?? []).map((learner) => {
      let person = learner.getProperties([
        'id',
        'jurisdictionalId',
        'firstName',
        'lastName',
        'currentYearLevel',
        'gender',
        'jurisdictionalEthnicity',
      ]);

      let row = [
        person.jurisdictionalId,
        person.firstName,
        person.lastName,
        person.currentYearLevel,
        person.gender,
        person.jurisdictionalEthnicity,
      ];

      let milestones = this.get(`learnerMilestones.${person.id}`);
      this.subjects.forEach((subject) => {
        let level = this.computeCurrentLevel(
          person,
          subject.id,
          milestones[subject.id],
        );
        if (isEmpty(level)) {
          row.push('');
        } else {
          row.push(level.get('label'));
        }
      });

      return row;
    });
  }

  @action
  selectMilestoneSet(milestoneSet) {
    this.selectedMilestoneSet = milestoneSet;
  }

  @action
  getCurrentLevel(person, subjectId) {
    return this.get(`learnerLevels.${person.id}.${subjectId}`);
  }

  @action
  getLevels(subjectId) {
    return filterBy(this.model.levels, this.filterField, subjectId).sort(
      byKeys('sequence', 'label'),
    );
  }

  @action
  getSummary(subjectId) {
    let levels = filterBy(this.model.levels, this.filterField, subjectId).sort(
      byKeys('sequence', 'label'),
    );
    let currentRecords = this.getCurrentLevels(subjectId);
    let summary = [];

    // Build up array of level names and counts.
    levels.forEach((level) => {
      summary.push({
        level,
        count: currentRecords.filter((item) => item.levelId === level.id)
          .length,
      });
    });

    return summary;
  }

  @action
  getSummaryByField(subjectId, field) {
    let levels = filterBy(this.model.levels, this.filterField, subjectId).sort(
      byKeys('sequence', 'label'),
    );
    let currentRecords = this.getCurrentLevels(subjectId);
    let summary = [];
    let values = uniq(mapBy(this.model.learners, field));
    // Build up array of level names and counts, per unique field value.
    values.forEach((value) => {
      let data = [];
      let records = filterBy(currentRecords, `person.${field}`, value);
      levels.forEach((level) => {
        data.push({
          level,
          count: records.filter((item) => item.levelId === level.id).length,
        });
      });
      summary.push({
        label: value,
        levels: data,
      });
    });

    return summary;
  }

  @action
  getPercentageOfLearners(num) {
    let result = 0;
    let numLearners = this.model.learners.length;
    if (num <= 0 || numLearners <= 0) {
      return result;
    }

    result = (num / numLearners) * 100;
    return result.toFixed(2);
  }

  computeCurrentLevel(person, subjectId, currentMilestone = null) {
    let progressions = this.get(
      `learnerProgressions.${person.id}.${subjectId}`,
    );
    if (isEmpty(progressions)) {
      // No progression data for this learner.
      return;
    }

    if (isEmpty(currentMilestone)) {
      // If not specified, try looking up the learner's current milestone.
      currentMilestone = this.get(
        `learnerMilestones.${person.id}.${subjectId}`,
      );
      if (isEmpty(currentMilestone)) {
        // Couldn't determine milestone.  Don't display anything.
        return;
      }
    }

    // Find current milestone's progression, if there is one.
    // Otherwise, backtrack until we find one or run out of milestones.
    let record = progressions.find(
      (item) => item.milestone_id === currentMilestone.id,
    );
    let milestones = [];
    let subject = this.subjects.find((item) => item.id === subjectId);
    if (!isEmpty(subject) && !isEmpty(subject.milestones)) {
      milestones = subject.milestones;
    }
    let milestoneIndex = milestones.indexOf(currentMilestone);
    while (isEmpty(record) && milestoneIndex > 0) {
      milestoneIndex--;
      let prevMilestone = milestones[milestoneIndex];
      record = progressions.find(
        (item) => item.milestone_id === prevMilestone.id,
      );
    }

    if (isEmpty(record)) {
      // No progression record found.
      return;
    }

    return record.get('level');
  }

  getCurrentLevels(entityId) {
    let currentRecords = [];

    this.model.learners.forEach((learner) => {
      let current = this.computeCurrentLevel(learner, entityId);
      if (!isEmpty(current)) {
        currentRecords.push({
          person: learner,
          levelId: current.get('id'),
        });
      }
    });

    return currentRecords;
  }
}
